import type { TFunction } from 'i18next';
import type { ISchedulerTask } from '@interfaces/IScheduler';

// ---------------------------------------------------------------------------
// What "Take back commitment" would delete. Taking a promise back deletes its
// execution record outright — progress, comments, ticked checks, stock claims
// (the server drops the overlay). The ladder only offers it while the field
// has recorded no work time, but a promise can still hold what PEOPLE wrote on
// it; that is worth one confirmation, with the counts, before it goes (D5).
// ---------------------------------------------------------------------------

export interface ITakeBackImpact {
  progressPercent: number;
  comments: number;
  checks: number;
  stockClaims: number;
}

type ExecutionFacts = Pick<ISchedulerTask, 'progressPercent' | 'comments' | 'checklist' | 'stockClaims'>;

/** What a take-back would delete from `task`, or null when there is nothing to lose. */
export function takeBackImpact(task: ExecutionFacts): ITakeBackImpact | null {
  const impact: ITakeBackImpact = {
    progressPercent: task.progressPercent,
    comments: task.comments?.length ?? 0,
    checks: (task.checklist ?? []).filter((item) => item.completion?.checked).length,
    stockClaims: task.stockClaims?.length ?? 0,
  };
  return Object.values(impact).some((count) => count > 0) ? impact : null;
}

/** The impact as one line for the confirmation: only what is there to lose. */
export function takeBackImpactLine(impact: ITakeBackImpact, t: TFunction): string {
  const parts = [
    impact.progressPercent > 0 && t('scheduler.commit.takeBack.progress', { percent: impact.progressPercent }),
    impact.comments > 0 && t('scheduler.commit.takeBack.comments', { count: impact.comments }),
    impact.checks > 0 && t('scheduler.commit.takeBack.checks', { count: impact.checks }),
    impact.stockClaims > 0 && t('scheduler.commit.takeBack.stockClaims', { count: impact.stockClaims }),
  ].filter((part): part is string => typeof part === 'string');
  return t('scheduler.commit.takeBack.body', { what: parts.join(' · ') });
}
